import React from 'react';

import { If } from '@kit/ui/if';
import { Input, InputProps } from '@kit/ui/input';
import { cn } from '@kit/ui/utils';

const ClearButton: React.FC<{
  className?: string;
}> = ({ className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      className={cn('h-5 w-5 transition-opacity hover:opacity-80', className)}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.66675 10.0804C1.66675 5.47803 5.39771 1.74707 10.0001 1.74707C14.6025 1.74707 18.3334 5.47803 18.3334 10.0804C18.3334 14.6828 14.6025 18.4137 10.0001 18.4137C5.39771 18.4137 1.66675 14.6828 1.66675 10.0804ZM8.08934 6.99115C7.7639 6.66571 7.23626 6.66571 6.91083 6.99115C6.58539 7.31659 6.58539 7.84422 6.91083 8.16966L8.82157 10.0804L6.91083 11.9911C6.58539 12.3166 6.58539 12.8442 6.91083 13.1697C7.23626 13.4951 7.7639 13.4951 8.08934 13.1697L10.0001 11.2589L11.9108 13.1697C12.2363 13.4951 12.7639 13.4951 13.0893 13.1697C13.4148 12.8442 13.4148 12.3166 13.0893 11.9911L11.1786 10.0804L13.0893 8.16966C13.4148 7.84422 13.4148 7.31659 13.0893 6.99115C12.7639 6.66571 12.2363 6.66571 11.9108 6.99115L10.0001 8.90189L8.08934 6.99115Z"
        fill="url(#paint0_linear_567_15555)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_567_15555"
          x1="10.0001"
          y1="1.74707"
          x2="10.0001"
          y2="18.4137"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F8C9D2" />
          <stop offset="1" stopColor="#F36B85" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const EyeButton: React.FC<{
  className?: string;
  color?: string;
  on?: boolean;
  active?: boolean;
}> = ({ className, color, on, active }) => {
  const iconClasses = cn(
    'h-4 w-4 transition-all',
    active ? 'opacity-100' : 'opacity-40',
    className,
  );

  if (on) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 17"
        fill="none"
        className={iconClasses}
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.99999 2.74707C10.8261 2.74705 13.5591 4.48786 15.2588 7.77409L15.4172 8.08037L15.2588 8.38664C13.5591 11.6729 10.8261 13.4137 8 13.4137C5.17389 13.4138 2.4409 11.6729 0.741178 8.38671L0.582764 8.08044L0.741178 7.77416C2.4409 4.48794 5.17389 2.74709 7.99999 2.74707ZM5.66667 8.0804C5.66667 6.79174 6.71134 5.74707 8 5.74707C9.28866 5.74707 10.3333 6.79174 10.3333 8.0804C10.3333 9.36907 9.28866 10.4137 8 10.4137C6.71134 10.4137 5.66667 9.36907 5.66667 8.0804Z"
          fill={color ?? '#fff'}
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 17"
      fill="none"
      className={iconClasses}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.00018 1.1377L14.943 14.0805L14.0002 15.0233L11.4685 12.4916C9.70167 13.4766 7.71873 13.6766 5.85443 13.0709C3.8287 12.4126 2.00196 10.824 0.741178 8.38647L0.582764 8.08019L0.741178 7.77391C1.46546 6.37359 2.37592 5.25362 3.40537 4.42851L1.05737 2.0805L2.00018 1.1377ZM5.33333 8.08023C5.33333 7.586 5.46818 7.12269 5.70261 6.72574L6.7117 7.73483C6.6823 7.84494 6.66667 7.96069 6.66667 8.08023C6.66667 8.81661 7.26362 9.41356 8 9.41356C8.11954 9.41356 8.23529 9.39792 8.3454 9.36853L9.35449 10.3776C8.95754 10.612 8.49423 10.7469 8 10.7469C6.52724 10.7469 5.33333 9.55299 5.33333 8.08023Z"
        fill="white"
      />
      <path
        d="M15.2587 8.38664C14.7728 9.32612 14.2024 10.1393 13.5695 10.8216L5.84123 3.09331C6.54368 2.86362 7.26865 2.74708 7.99986 2.74707C10.826 2.74705 13.5589 4.48786 15.2587 7.77409L15.4171 8.08037L15.2587 8.38664Z"
        fill="white"
      />
    </svg>
  );
};

const InputAuth = React.forwardRef<
  HTMLInputElement,
  InputProps & { error?: boolean }
>(({ error, ...restProps }, ref) => {
  const [errorState, setErrorState] = React.useState<boolean>(error ?? false);
  const [focused, setFocused] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [prevError, setPrevError] = React.useState(error);

  if (error !== prevError) {
    setPrevError(error);
    setErrorState(error ?? false);
  }

  const containerClasses = cn(
    'rounded-full p-[1px]',
    errorState
      ? 'bg-[#E48787]'
      : 'bg-[#484956] focus-within:bg-gradient-to-b focus-within:from-[#377EBA] focus-within:to-[rgba(255,255,255,0.5)]',
  );

  const inputWrapperClasses = cn(
    'flex h-11 items-center gap-2',
    'rounded-full bg-[#30313b] px-[18px] py-1',
    'shadow-sm backdrop-blur-[12px]',
  );

  const inputClasses = cn(
    'flex-1 rounded-none border-none bg-transparent p-0',
    'text-sm leading-5 font-medium',
    'text-white placeholder:text-white/50',
    'focus-visible:ring-0',
    '[&:-webkit-autofill]:bg-[#30313b]',
    '[&:-webkit-autofill]:text-white',
    '[&:-webkit-autofill]:[-webkit-text-fill-color:white]',
    '[&:-webkit-autofill]:caret-white',
    '[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#30313b]',
    '[&:-webkit-autofill]:hover:shadow-[inset_0_0_0_1000px_#30313b]',
    '[&:-webkit-autofill]:focus:shadow-[inset_0_0_0_1000px_#30313b]',
    '[&:-webkit-autofill]:active:shadow-[inset_0_0_0_1000px_#30313b]',
    restProps.className,
  );

  return (
    <div className={containerClasses}>
      <div className={inputWrapperClasses}>
        <Input
          {...restProps}
          className={inputClasses}
          ref={ref}
          onFocus={(event) => {
            restProps.onFocus?.(event);
            setFocused(true);
          }}
          onBlur={(event) => {
            restProps.onBlur?.(event);
            setFocused(false);
          }}
          type={showPassword ? 'text' : restProps.type}
        />

        <If condition={!!errorState}>
          <button
            type="button"
            className="flex items-center justify-center transition-opacity hover:opacity-80"
            onClick={() => {
              restProps.onChange?.({
                target: { value: '' },
              } as React.ChangeEvent<HTMLInputElement>);
              setErrorState(false);
            }}
            aria-label="Clear input"
          >
            <ClearButton />
          </button>
        </If>

        <If condition={!errorState && restProps.type === 'password'}>
          <button
            type="button"
            tabIndex={-1}
            className="flex items-center justify-center transition-opacity hover:opacity-80"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <EyeButton active={focused} on={showPassword} />
          </button>
        </If>
      </div>
    </div>
  );
});

InputAuth.displayName = 'InputAuth';

export default InputAuth;
